<template>
  <div :class="cn('mx-auto w-full max-w-[600px] h-[370px] sm:h-[600px] -mt-[80px] md:mt-0 overflow-hidden', $props.class)">
    <canvas
        ref="globeCanvasRef"
        class="w-full h-[370px] sm:h-[600px] opacity-0 transition-opacity duration-1000 ease-in-out [contain:layout_paint_size]"
        @pointerdown="(e) => updatePointerInteraction(e.clientX)"
        @pointerup="updatePointerInteraction(null)"
        @pointerout="updatePointerInteraction(null)"
        @mousemove="(e) => updateMovement(e.clientX)"
        @touchmove="(e) => e.touches[0] && updateMovement(e.touches[0].clientX)"
    ></canvas>
  </div>
</template>

<script setup>
import { cn } from "../../lib/utils";
import createGlobe from "cobe";
import { useSpring } from "vue-use-spring";
import { ref, onMounted, onBeforeUnmount } from "vue";

const DEFAULT_CONFIG = {
  width: 800,
  height: 800,
  onRender: () => {},
  devicePixelRatio: 2,
  phi: 0,
  theta: 0.3,
  dark: 0,
  diffuse: 0.4,
  mapSamples: 16000,
  mapBrightness: 1.2,
  baseColor: [1, 1, 1],
  markerColor: [251 / 255, 100 / 255, 21 / 255],
  glowColor: [1.2, 1.2, 1.2],
  markers: [
    // North America
    { location: [40.7128, -74.006], size: 0.08 },   // New York
    { location: [34.0522, -118.2437], size: 0.07 }, // Los Angeles
    { location: [41.8781, -87.6298], size: 0.06 },  // Chicago
    { location: [43.6532, -79.3832], size: 0.06 },  // Toronto
    { location: [49.2827, -123.1207], size: 0.05 }, // Vancouver
    { location: [19.4326, -99.1332], size: 0.06 },  // Mexico City
    
    // South America
    { location: [-23.5505, -46.6333], size: 0.06 }, // São Paulo
    { location: [-22.9068, -43.1729], size: 0.05 }, // Rio de Janeiro
    { location: [-34.6037, -58.3816], size: 0.05 }, // Buenos Aires
    { location: [-33.4489, -70.6693], size: 0.04 }, // Santiago
    { location: [-12.0464, -77.0428], size: 0.04 }, // Lima
    
    // Europe
    { location: [51.5074, -0.1278], size: 0.08 },   // London
    { location: [48.8566, 2.3522], size: 0.07 },    // Paris
    { location: [52.5200, 13.4050], size: 0.06 },   // Berlin
    { location: [41.9028, 12.4964], size: 0.06 },   // Rome
    { location: [40.4168, -3.7038], size: 0.05 },   // Madrid
    { location: [52.3676, 4.9041], size: 0.05 },    // Amsterdam
    { location: [59.3293, 18.0686], size: 0.04 },   // Stockholm
    { location: [60.1699, 24.9384], size: 0.04 },   // Helsinki
    { location: [55.6761, 12.5683], size: 0.04 },   // Copenhagen
    
    // Africa
    { location: [-26.2041, 28.0473], size: 0.05 },  // Johannesburg
    { location: [-33.9249, 18.4241], size: 0.04 },  // Cape Town
    { location: [30.0444, 31.2357], size: 0.04 },   // Cairo
    { location: [-1.2921, 36.8219], size: 0.03 },   // Nairobi
    { location: [6.5244, 3.3792], size: 0.03 },     // Lagos
    
    // Asia
    { location: [35.6762, 139.6503], size: 0.08 },  // Tokyo
    { location: [37.5665, 126.9780], size: 0.06 },  // Seoul
    { location: [39.9042, 116.4074], size: 0.07 },  // Beijing
    { location: [31.2304, 121.4737], size: 0.06 },  // Shanghai
    { location: [22.3193, 114.1694], size: 0.05 },  // Hong Kong
    { location: [28.7041, 77.1025], size: 0.06 },   // Delhi
    { location: [19.076, 72.8777], size: 0.05 },    // Mumbai
    { location: [13.7563, 100.5018], size: 0.04 },  // Bangkok
    { location: [1.3521, 103.8198], size: 0.05 },   // Singapore
    { location: [-6.2088, 106.8456], size: 0.04 },  // Jakarta
    { location: [14.5995, 120.9842], size: 0.04 },  // Manila
    
    // Middle East
    { location: [25.2048, 55.2708], size: 0.04 },   // Dubai
    { location: [29.3117, 47.4818], size: 0.03 },   // Kuwait City
    { location: [24.7136, 46.6753], size: 0.03 },   // Riyadh
    { location: [33.8869, 35.5131], size: 0.03 },   // Beirut
    { location: [41.0082, 28.9784], size: 0.04 },   // Istanbul
    
    // Oceania
    { location: [-33.8688, 151.2093], size: 0.07 }, // Sydney
    { location: [-37.8136, 144.9631], size: 0.06 }, // Melbourne
    { location: [-27.4698, 153.0251], size: 0.05 }, // Brisbane
    { location: [-36.8485, 174.7633], size: 0.05 }, // Auckland
    { location: [-17.7134, 168.3273], size: 0.03 }, // Port Vila
    
    // Russia & Eastern Europe
    { location: [55.7558, 37.6176], size: 0.06 },   // Moscow
    { location: [59.9311, 30.3609], size: 0.05 },   // St. Petersburg
    { location: [50.4501, 30.5234], size: 0.04 },   // Kyiv
    { location: [52.2297, 21.0122], size: 0.04 },   // Warsaw
    { location: [47.4979, 19.0402], size: 0.04 },   // Budapest
    
    // Additional scattered points for fullness
    { location: [64.1466, -21.9426], size: 0.03 },  // Reykjavik
    { location: [9.0579, 8.6753], size: 0.03 },     // Abuja
    { location: [15.5007, 32.5599], size: 0.03 },   // Khartoum
    { location: [26.8206, 30.8025], size: 0.03 },   // Luxor
    { location: [43.7696, 11.2558], size: 0.03 },   // Florence
    { location: [41.3275, 19.8187], size: 0.03 },   // Tirana
    { location: [42.6977, 23.3219], size: 0.03 },   // Sofia
    { location: [44.4268, 26.1025], size: 0.03 },   // Bucharest
  ],
};

const props = defineProps({
  class: {
    type: String,
    default: ""
  },
  config: {
    type: Object,
    default: () => ({})
  },
  mass: {
    type: Number,
    default: 1
  },
  tension: {
    type: Number,
    default: 280
  },
  friction: {
    type: Number,
    default: 100
  },
  precision: {
    type: Number,
    default: 0.001
  }
});

const globeCanvasRef = ref();
const phi = ref(0);
const width = ref(0);
const pointerInteracting = ref();
const pointerInteractionMovement = ref();

let globe = null;

const spring = useSpring(
    {
      r: 0,
    },
    {
      mass: props.mass,
      tension: props.tension,
      friction: props.friction,
      precision: props.precision,
    },
);

function updatePointerInteraction(clientX) {
  if (clientX !== null) {
    pointerInteracting.value = clientX - (pointerInteractionMovement.value ?? clientX);
  } else {
    pointerInteracting.value = null;
  }

  if (globeCanvasRef.value) {
    globeCanvasRef.value.style.cursor = clientX ? "grabbing" : "grab";
  }
}

function updateMovement(clientX) {
  if (pointerInteracting.value !== null) {
    const delta = clientX - (pointerInteracting.value ?? clientX);
    pointerInteractionMovement.value = delta;
    spring.r = delta / 200;
  }
}

function onRender(state) {
  if (!pointerInteracting.value) {
    phi.value += 0.005;
  }

  state.phi = phi.value + spring.r;
  state.width = width.value * 2;
  state.height = width.value * 2;
}

function onResize() {
  if (globeCanvasRef.value) {
    width.value = globeCanvasRef.value.offsetWidth;
  }
}

function createGlobeOnMounted() {
  const config = { ...DEFAULT_CONFIG, ...props.config };

  globe = createGlobe(globeCanvasRef.value, {
    ...config,
    width: width.value * 2,
    height: width.value * 2,
    onRender,
    markerColor: [0 / 255, 0 / 255, 0 / 255],
  });
}

onMounted(() => {
  window.addEventListener("resize", onResize);
  onResize();
  createGlobeOnMounted();

  setTimeout(() => (globeCanvasRef.value.style.opacity = "1"));
});

onBeforeUnmount(() => {
  if (globe) {
    globe.destroy();
  }
  window.removeEventListener("resize", onResize);
});
</script>